import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LayoutGrid, Search, Package, LogOut, Store, RefreshCw, ShoppingBag } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { cjConnectionStatus, syncStatus } from "../../lib/api";

const NAV = [
  { to: "/admin/cj-catalog", label: "Catalogue CJ", icon: Search, testid: "admin-nav-catalog" },
  { to: "/admin/products", label: "Mes produits", icon: Package, testid: "admin-nav-products" },
  { to: "/admin/orders", label: "Commandes", icon: ShoppingBag, testid: "admin-nav-orders" },
  { to: "/admin/sync-dashboard", label: "Synchronisation", icon: RefreshCw, testid: "admin-nav-sync" },
];

const CjStatusBadge = () => {
  const [status, setStatus] = useState(null); // null=loading, true/false
  const [msg, setMsg] = useState("");
  const [mode, setMode] = useState(null); // 'mock' | 'real' | null
  useEffect(() => {
    let alive = true;
    cjConnectionStatus()
      .then((d) => { if (alive) { setStatus(!!d.connected); setMsg(d.message || ""); } })
      .catch(() => { if (alive) { setStatus(false); setMsg("Statut indisponible"); } });
    syncStatus()
      .then((d) => { if (alive) setMode(d?.settings?.cj_mode || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (mode === "mock") {
    return (
      <div title="Mode simulation : données CJ mockées, aucun appel réel." className="flex items-center gap-2 border px-3 py-1.5 text-[11px] uppercase tracking-wider text-amber-700 border-amber-200 bg-amber-50" data-testid="cj-status-badge">
        <span className="w-2 h-2 rounded-full bg-amber-500" />
        Mode simulation
      </div>
    );
  }

  const cfg = status === null
    ? { dot: "bg-ardoise/40 animate-pulse", label: "Vérification CJ…", cls: "text-ardoise/70 border-dore/20" }
    : status
      ? { dot: "bg-green-500", label: "CJ connecté", cls: "text-green-700 border-green-200 bg-green-50" }
      : { dot: "bg-red-500", label: "CJ déconnecté", cls: "text-red-700 border-red-200 bg-red-50" };

  return (
    <div title={msg} className={`flex items-center gap-2 border px-3 py-1.5 text-[11px] uppercase tracking-wider ${cfg.cls}`} data-testid="cj-status-badge">
      <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </div>
  );
};

export default function AdminShell({ children, title }) {
  const { user, loading, logout } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-ardoise">Chargement...</div>;
  }
  if (!user) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="admin-need-login">
        <h1 className="font-display text-2xl text-encre">Espace administration</h1>
        <p className="text-ardoise text-sm">Vous devez être connectée pour accéder à cette page.</p>
        <Link to="/compte" className="bg-noir text-white px-6 py-3 text-xs uppercase tracking-widest">Se connecter</Link>
      </div>
    );
  }
  if (!user.is_admin) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="admin-forbidden">
        <h1 className="font-display text-2xl text-encre">Accès réservé</h1>
        <p className="text-ardoise text-sm">Cet espace est réservé à l'administration de la boutique.</p>
        <Link to="/" className="text-dore underline text-sm">Retour à l'accueil</Link>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] max-w-7xl mx-auto px-4 sm:px-6 py-8 grid md:grid-cols-[220px_1fr] gap-6" data-testid="admin-shell">
      <aside className="md:sticky md:top-24 self-start">
        <div className="flex items-center gap-2 mb-6 px-2">
          <LayoutGrid size={18} className="text-dore" />
          <span className="font-display text-lg text-encre">Admin</span>
        </div>
        <nav className="flex md:flex-col gap-1">
          {NAV.map((n) => {
            const active = location.pathname.startsWith(n.to);
            const Icon = n.icon;
            return (
              <Link key={n.to} to={n.to} data-testid={n.testid}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${active ? "bg-noir text-white" : "text-encre hover:bg-dore/10"}`}>
                <Icon size={16} /> {n.label}
              </Link>
            );
          })}
          <Link to="/" data-testid="admin-nav-store" className="flex items-center gap-3 px-3 py-2.5 text-sm text-encre hover:bg-dore/10 transition-colors">
            <Store size={16} /> Voir la boutique
          </Link>
          <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-3 px-3 py-2.5 text-sm text-encre hover:bg-dore/10 transition-colors text-left">
            <LogOut size={16} /> Déconnexion
          </button>
        </nav>
      </aside>
      <main>
        {title && (
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <h1 className="font-display text-2xl sm:text-3xl text-encre" data-testid="admin-page-title">{title}</h1>
            <CjStatusBadge />
          </div>
        )}
        {children}
      </main>
    </div>
  );
}
